import { ArrowUpRight, CheckDone02 } from "@untitledui/icons";
import { useAssuranceOverviewV2 } from "@/api/assurance/assurance-overview";
import { Link } from "@/components/link";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import { Skeleton } from "@/components/ui/skeleton";
import { useScopedUrl } from "@/hooks/use-scoped-url";
import { getStartDateISO } from "@/utils/date-conversion";
import { GlassMetricTile } from "./GlassMetricTile";
import { SafetyEmptyState } from "./SafetyEmptyState";
import type { SafetyGraphItem } from "./SafetyGraph";

// No period selector on this card — "Average time in queue" uses a fixed L30D
// window per the ticket's default assumption (confirm with product, see falta-por-hacer.md)
const AVG_QUEUE_WINDOW_DAYS = 30;

// Fixed display order + colors for the 4 severity buckets — tierToSeverity
// collapses any real (unbounded) risk_tier value into one of these 4 buckets.
// This chart's colors are its own (not the shared SEVERITY_COLOR) on purpose.
const SEVERITY_ORDER = [
  { severity: "critical", label: "Critical", fill: "#f04438" },
  { severity: "high", label: "High", fill: "#ff692e" },
  { severity: "medium", label: "Medium", fill: "#fdb022" },
  { severity: "low", label: "Low", fill: "#fedf89" },
] as const;

export const NeedsAttentionCard = () => {
  const { data, isLoading } = useAssuranceOverviewV2({ startDate: getStartDateISO(AVG_QUEUE_WINDOW_DAYS) });
  const scopedUrl = useScopedUrl();

  const videosAwaitingReview = data?.videosAwaitingReview ?? 0;
  const avgQueueDays =
    data?.averageResolutionTime?.find((t) => t.fromStatus === "needs_reviewing" && t.toStatus === "needs_action")
      ?.averageDays ?? 0;

  const severityTotals = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const { tier, count } of data?.severityCounts ?? []) {
    severityTotals[tierToSeverity(tier ?? 4)] += count;
  }
  const severityData: SafetyGraphItem[] = SEVERITY_ORDER.map(({ severity, label, fill }) => ({
    label,
    value: severityTotals[severity],
    fill,
  }));

  const isEmpty = videosAwaitingReview === 0 && avgQueueDays === 0 && severityData.every((item) => item.value === 0);

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center gap-2">
        <CheckDone02 className="size-5 text-secondary" />
        <p className="text-base text-secondary font-medium">Needs attention</p>
      </div>
      {isLoading || !data ? (
        <Skeleton className="h-[315px] w-full rounded-2xl" />
      ) : isEmpty ? (
        <SafetyEmptyState
          title="No videos awaiting review"
          subtitle="Nothing needs your attention right now — the review queue is empty."
          width="100%"
          height="315px"
        />
      ) : (
        <div
          className="flex w-full rounded-2xl overflow-hidden"
          style={{ background: "linear-gradient(160deg, #322047 0%, #4B2E61 50%, #3D2653 100%)" }}
        >
          <div className="flex flex-col gap-5 justify-center p-8 w-[347px] shrink-0">
            <GlassMetricTile
              label="Videos awaiting review"
              value={String(videosAwaitingReview)}
              caption={
                <Link
                  to={`${scopedUrl}/flagged-videos` as string}
                  search={{ tab: "needs-review" }}
                  className="flex items-center gap-0.5 cursor-pointer"
                >
                  <span className="text-xs text-white/60 underline decoration-dotted">Review Videos</span>
                  <ArrowUpRight className="size-4 text-white/60" />
                </Link>
              }
              tooltip={`The total number of videos currently sitting in the "needs review" queue.`}
            />
            <GlassMetricTile
              label="Average time in queue"
              value={String(avgQueueDays)}
              caption={<span className="text-xs text-white/60">Days</span>}
              tooltip={`The average elapsed time from when a video first enters the "needs review" queue to when it leaves the queue. Shown in days (e.g. 2.4 Days)`}
            />
          </div>
          <div className="flex flex-col gap-6 flex-1 min-w-0 px-8 pt-8 pb-10">
            <p className="text-sm text-white shrink-0">Review queue by severity</p>
            <SeverityBars data={severityData} />
          </div>
        </div>
      )}
    </div>
  );
};

const SeverityBars = ({ data }: { data: SafetyGraphItem[] }) => {
  const max = Math.max(...data.map((item) => item.value));

  return (
    <div className="flex flex-1 flex-col gap-4 w-full">
      {data.map((item) => (
        <div key={item.label} className="flex flex-1 items-center gap-3 w-full">
          <p className="text-xs shrink-0 w-[70px] text-white/90">{item.label}</p>
          <div className="relative flex-1 h-full min-w-0 rounded overflow-hidden bg-white/15">
            <div
              className="absolute inset-y-0 left-0 rounded"
              style={{ width: `${max > 0 ? (item.value / max) * 100 : 0}%`, background: item.fill as string }}
            />
          </div>
          <p className="text-xs w-[36px] text-right shrink-0 text-white">{item.value}</p>
        </div>
      ))}
    </div>
  );
};
